/**
 * Convert decimal hours to a formatted string showing hours and minutes
 * @param {number|string} decimalHours - Hours in decimal format 
 *   If formatted like 1.5, it means 1 hour 30 minutes (traditional decimal)
 *   If formatted like 1.20, it means 1 hour 20 minutes (direct minutes interpretation)
 * @returns {string} Formatted string like "1 Hour 30 Minutes" or "1 Hours 30 Minutes"
 */
export const formatHoursToHoursMinutes = (decimalHours) => {
  if (!decimalHours || isNaN(decimalHours)) return '0 Hours';
  
  // Special case for hours already in "X Hours Y Minutes" format - don't reprocess
  if (typeof decimalHours === 'string' && 
      (decimalHours.includes('Hour') || decimalHours.includes('Hours') || 
       decimalHours.includes('Minute') || decimalHours.includes('Minutes'))) {
    return decimalHours;
  }
  
  // First, normalize the hours value using correctHoursDisplay to ensure consistency
  // This is crucial to handle different input formats from various components
  const normalizedHoursStr = correctHoursDisplay(decimalHours);
  
  let wholeHours = 0;
  let minutes = 0;
  
  // Now parse the normalized string format which is guaranteed to be hours.minutes
  if (normalizedHoursStr.includes('.')) {
    const parts = normalizedHoursStr.split('.');
    wholeHours = parseInt(parts[0], 10) || 0;
    
    // The minutes part will always be 2 digits in the normalized format
    minutes = parseInt(parts[1], 10) || 0;
  } else {
    // No decimal part
    wholeHours = parseInt(normalizedHoursStr, 10) || 0;
    minutes = 0;
  }
  
  // Format the string
  let result = '';
  
  if (wholeHours > 0) {
    result += `${wholeHours} ${wholeHours === 1 ? 'Hour' : 'Hours'}`;
  }
  
  if (minutes > 0) {
    result += `${wholeHours > 0 ? ' ' : ''}${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`;
  }
  
  // If both hours and minutes are 0, return 0 Hours
  if (wholeHours === 0 && minutes === 0) {
    result = '0 Hours';
  }
  
  return result;
};

/**
 * Format decimal hours in a way that maintains the hour/minute representation
 * @param {number|string} hours - Hours in decimal or hour.minute format
 * @returns {string} Formatted string like "1.20" meaning 1 hour 20 minutes
 */
export const formatDecimalHours = (hours) => {
  if (!hours || isNaN(hours)) return '0.00';
  
  // Handle the hours as a string to check format
  const hoursStr = hours.toString();
  
  // Check if the format already includes a decimal point
  if (hoursStr.includes('.')) {
    const parts = hoursStr.split('.');
    let wholeHours = parseInt(parts[0], 10) || 0;
    let minutes = parts[1];
    
    // If decimal part has exactly 2 digits, treat as direct minutes
    if (minutes.length === 2) {
      const parsedMinutes = parseInt(minutes, 10) || 0;
      // Handle when minutes >= 60
      if (parsedMinutes >= 60) {
        wholeHours += Math.floor(parsedMinutes / 60);
        minutes = parsedMinutes % 60;
        // Format as hour.minutes with minutes as two digits
        return `${wholeHours}.${minutes.toString().padStart(2, '0')}`;  
      }
      // Otherwise it's already in the right format
      return hoursStr;
    } else if (minutes.length === 1) {
      // If it's a single digit decimal, treat as tenths of an hour (e.g., 0.5 = 30 minutes)
      const parsedMinutes = Math.round((parseFloat(`0.${minutes}`) * 60));
      
      // Handle rollover when minutes >= 60
      if (parsedMinutes >= 60) {
        wholeHours += Math.floor(parsedMinutes / 60);
        minutes = parsedMinutes % 60;
      } else {
        minutes = parsedMinutes;
      }
      
      // Ensure minutes is two digits
      minutes = minutes.toString().padStart(2, '0');
      return `${wholeHours}.${minutes}`;
    } else {
      // For other decimal formats, convert to minutes
      const parsedMinutes = Math.round((parseFloat(`0.${minutes}`) * 60));
      
      // Handle rollover when minutes >= 60
      if (parsedMinutes >= 60) {
        wholeHours += Math.floor(parsedMinutes / 60);
        minutes = parsedMinutes % 60;
      } else {
        minutes = parsedMinutes;
      }
      
      // Ensure minutes is two digits
      minutes = minutes.toString().padStart(2, '0');
      return `${wholeHours}.${minutes}`;
    }
  } else {
    // No decimal part, return with .00
    return `${parseInt(hoursStr, 10) || 0}.00`;
  }
};

/**
 * Corrects hours worked value for display in reports
 * Ensures that the hour/minute format is properly handled (minutes > 59 roll over)
 * @param {number|string} hoursWorked - Hours in any format
 * @returns {string} Formatted string in hour.minutes format with proper rollover
 */
export const correctHoursDisplay = (hoursWorked) => {
  if (!hoursWorked || isNaN(hoursWorked)) return '0.00';
  
  const hoursStr = hoursWorked.toString().trim();
  
  // If no decimal, return as is with .00
  if (!hoursStr.includes('.')) {
    return `${parseInt(hoursStr, 10) || 0}.00`;
  }
  
  const [hours, minutesStr] = hoursStr.split('.');
  const wholeHours = parseInt(hours, 10) || 0;
  
  // Handle different minute formats
  let minutes;
  if (minutesStr.length === 2) {
    // If it's a 2-digit value, treat as direct minutes
    minutes = parseInt(minutesStr, 10) || 0;
  } else if (minutesStr.length === 1) {
    // If it's a single digit, multiply by 10 to get minutes
    minutes = parseInt(minutesStr, 10) * 10 || 0;
  } else {
    // If it's something else, convert from decimal to minutes
    minutes = Math.round(parseFloat(`0.${minutesStr}`) * 60);
  }
  
  // Apply rollover when minutes >= 60
  if (minutes >= 60) {
    const additionalHours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${wholeHours + additionalHours}.${remainingMinutes.toString().padStart(2, '0')}`;
  }
  
  // Return in standardized format
  return `${wholeHours}.${minutes.toString().padStart(2, '0')}`;
};

/**
 * Calculate total hours from task details - EXACT SAME LOGIC AS EDIT REPORT
 * This ensures consistent hour calculation across all views
 * @param {Array} taskDetails - Array of task details with hours
 * @returns {string|number} Total hours in decimal format
 */
export const calculateTotalHours = (taskDetails) => {
  let totalHours = 0;
  let totalMinutes = 0;
  
  if (!taskDetails || taskDetails.length === 0) return '0.00';
  
  taskDetails.forEach(task => {
    if (!task.hours) return;
    
    const hoursStr = task.hours.toString();
    
    if (hoursStr.includes('.')) {
      const parts = hoursStr.split('.');
      const wholeHours = parseInt(parts[0], 10) || 0;
      const decimalPart = parts[1];
      
      // Add the whole hours to total
      totalHours += wholeHours;
      
      // Always treat decimal part as direct minutes
      if (decimalPart.length === 1) {
        totalMinutes += parseInt(decimalPart, 10) * 10 || 0;
      } else {
        totalMinutes += parseInt(decimalPart, 10) || 0;
      }
    } else {
      // No decimal, just whole hours
      totalHours += parseInt(hoursStr, 10) || 0;
    }
  });
  
  // Convert excess minutes to hours
  if (totalMinutes >= 60) {
    totalHours += Math.floor(totalMinutes / 60);
    totalMinutes = totalMinutes % 60;
  }
  
  // Format the result as a string with two decimal places
  const formattedMinutes = totalMinutes.toString().padStart(2, '0');
  return `${totalHours}.${formattedMinutes}`;
};

/**
 * Get standardized hours for a report regardless of whether task_details are available
 * This is a critical function for ensuring hours are displayed consistently across all views
 * @param {Object} report - The report object
 * @returns {string} - The standardized hours in decimal format (e.g., '8.35')
 */
export const getStandardizedHours = (report) => {
  // If task details are available, calculate from them
  if (report.task_details && Array.isArray(report.task_details) && report.task_details.length > 0) {
    return calculateTotalHours(report.task_details);
  }
  
  // Fall back to stored hours if available
  if (report.hours_worked) {
    return typeof report.hours_worked === 'string' ? report.hours_worked : report.hours_worked.toString();
  }
  
  // Last resort
  return '0.00';
};
